import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { ArrowLeft, Search, Filter } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function PracticeOpsHistory() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [scenarioFilter, setScenarioFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/practice-ops/history");
        setRows(res.data || []);
      } catch (e) {
        setErr(e.response?.data?.detail || "Could not load history");
      }
    })();
  }, []);

  const scenarios = useMemo(() => {
    if (!rows) return [];
    const set = new Set();
    for (const r of rows) if (r.scenario_name) set.add(r.scenario_name);
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (scenarioFilter && r.scenario_name !== scenarioFilter) return false;
      if (!needle) return true;
      return [r.title, r.scenario_name, r.workflow_slug].filter(Boolean).some((v) => String(v).toLowerCase().includes(needle));
    });
  }, [rows, q, scenarioFilter]);

  return (
    <Layout wide>
      <div className="mb-6">
        <Link to="/practice-ops" data-testid="po-history-back"
          className="inline-flex items-center gap-1.5 text-sm text-brand-secondary hover:text-brand-ink transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
          Back to Practice Ops
        </Link>
      </div>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-brand-muted mb-1">Practice Ops</div>
        <h1 className="font-serif text-4xl text-brand-ink">History & archive</h1>
        <p className="text-brand-body text-sm mt-2">Completed workflows for your practice.</p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="po-history-search"
            placeholder="Search workflows…"
            className="w-full rounded-full border border-brand-linen bg-white pl-9 pr-4 py-2 text-sm placeholder:text-brand-muted focus:outline-none focus:border-brand-ink/40"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-brand-muted" strokeWidth={1.5} />
          <select
            data-testid="po-history-scenario-filter"
            value={scenarioFilter}
            onChange={(e) => setScenarioFilter(e.target.value)}
            className="rounded-full border border-brand-linen bg-white px-3 py-1.5 text-sm focus:outline-none focus:border-brand-ink/40"
          >
            <option value="">All scenarios</option>
            {scenarios.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {err && <div data-testid="po-history-error" className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 mb-4">{err}</div>}

      {filtered === null ? (
        <div className="text-brand-muted text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-brand-linen bg-white p-8 text-center text-brand-muted text-sm">
          {q || scenarioFilter ? "No workflows match your filters." : "No completed workflows yet."}
        </div>
      ) : (
        <div className="rounded-2xl border border-brand-linen bg-white overflow-hidden">
          <div className="grid grid-cols-12 gap-3 border-b border-brand-linen bg-brand-linen/40 px-5 py-3 text-[11px] font-medium uppercase tracking-widest text-brand-muted">
            <div className="col-span-5">Title</div>
            <div className="col-span-3">Scenario</div>
            <div className="col-span-2">Started</div>
            <div className="col-span-2">Completed</div>
          </div>
          {filtered.map((r) => (
            <Link
              key={r.id}
              to={`/practice-ops/instances/${r.id}`}
              data-testid={`po-history-row-${r.id}`}
              className="grid grid-cols-12 gap-3 border-b border-brand-linen last:border-b-0 px-5 py-3 text-sm hover:bg-brand-linen/20 transition-colors"
            >
              <div className="col-span-5 min-w-0">
                <div className="text-brand-ink truncate">{r.title || r.scenario_name}</div>
              </div>
              <div className="col-span-3 text-brand-secondary truncate">{r.scenario_name}</div>
              <div className="col-span-2 text-brand-muted text-xs">{fmtDate(r.started_at)}</div>
              <div className="col-span-2 text-brand-muted text-xs">{fmtDate(r.completed_at)}</div>
            </Link>
          ))}
        </div>
      )}
    </Layout>
  );
}
